import {
  Box,
  Button,
  ButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import React, { useEffect } from 'react'
import { LuX } from 'react-icons/lu'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'

import Chip from '@/components/Chip'
import HtmlKonvaElement from '@/components/HtmlKonvaElement'
import Icon from '@/components/Icon'
import LabelWithTooltip from '@/components/LabelWithTooltip'
import { useAppSelector } from '@/hooks/redux'
import {
  initializeFrameworkRecipeIndex,
  setFrameworkRecipeIndex,
} from '@/store/appSlice'

const TooltipContent = React.memo(
  ({
    output,
    setTooltipOpen,
    workflow,
  }: {
    output: any
    setTooltipOpen: () => void
    workflow: string
  }) => {
    const dispatch = useDispatch()
    const theme = useTheme()
    const location = useLocation()

    const canvasImage = useAppSelector((state) => state.app.canvasImage)
    const imageId = canvasImage?.id

    const recipeIndexByWorkflow = useAppSelector(
      (state) => state.app.frameworkRecipeIndexByWorkflow,
    )

    const selectedTab = recipeIndexByWorkflow?.[workflow]?.[imageId] ?? 0

    useEffect(() => {
      const hasOptions = Array.isArray(output?.frameworkadvice)
      if (hasOptions && imageId) {
        dispatch(initializeFrameworkRecipeIndex({ workflow, imageId }))
      }
    }, [workflow, imageId, output])

    const handleTabChange = (index: number) => {
      dispatch(setFrameworkRecipeIndex({ workflow, imageId, index }))
    }

    const isArrayOutput = Array.isArray(output?.frameworkadvice)
    const outputList = isArrayOutput
      ? output?.frameworkadvice
      : [output?.frameworkadvice]
    const selectedOutput = outputList[selectedTab]
    return (
      <Box display="flex" flexDirection="column" gap={2} width="100%">
        {location.pathname !== '/full-layering/framework' && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mt={1}
          >
            <LabelWithTooltip
              titleSx={{ fontSize: '14px' }}
              tooltipSx={{ height: '16px', width: '16px' }}
              label="Staining Target in LAB"
              tooltipText="Apply the staining recipe on the framework and measure before baking it with Optishade to verify the staining target in LAB values. The Chroma ( a*+b*) should be very close to the target. The Luminosity will be higher before baking it because it will drop after firing it."
            />
            <Icon
              icon={LuX}
              size={18}
              onClick={setTooltipOpen}
              style={{ cursor: 'pointer' }}
            />
          </Box>
        )}

        {isArrayOutput && (
          <ButtonGroup
            sx={{
              border: 'none',
              '& .MuiButtonGroup-grouped': {
                border: 'none',
              },
            }}
          >
            {outputList.map((_: any, index: number) => (
              <Button
                key={`${workflow}-option-${index}`}
                onClick={() => handleTabChange(index)}
                sx={{
                  borderRadius: '8px 8px 0px 0px',
                  backgroundColor:
                    selectedTab === index
                      ? 'rgba(144, 202, 249, 0.40)'
                      : '#2C2C2C',
                  color: selectedTab === index ? 'white' : '#9CA3AF',
                  textTransform: 'none',
                  boxShadow: 'none',
                  px: 2,
                  py: 1,
                  fontSize: '14px',
                  fontFamily: 'Inter',
                  fontWeight: 500,
                  '&.MuiButtonGroup-grouped': {
                    borderRight:
                      selectedTab !== index &&
                      selectedTab !== index + 1 &&
                      index !== outputList.length - 1
                        ? `2px solid #374151`
                        : 'none',
                  },
                }}
              >
                Option {index + 1}
              </Button>
            ))}
          </ButtonGroup>
        )}

        <Box display="flex" gap={1} flexWrap="wrap">
          {selectedOutput?.substructurename && (
            <>
              <Typography>Framework:</Typography>
              <Chip
                label={output?.provided_input?.capbrand}
                sx={{
                  bgcolor: theme.palette.background.chip,
                  color: theme.palette.common.black,
                  '&:hover': { bgcolor: theme.palette.background.chip },
                }}
                size="small"
              />
              <Chip
                label={selectedOutput.substructurename}
                sx={{
                  bgcolor: theme.palette.background.chip,
                  color: theme.palette.common.black,
                  '&:hover': { bgcolor: theme.palette.background.chip },
                }}
                size="small"
              />
            </>
          )}
        </Box>

        {selectedOutput?.thickness_cervical &&
          selectedOutput?.thickness_middle &&
          selectedOutput?.thickness_incisal && (
            <Box>
              <Table
                size="small"
                sx={{ minWidth: '100%', backgroundColor: 'transparent' }}
              >
                <TableBody>
                  <TableRow>
                    <TableCell
                      align="center"
                      sx={{ fontSize: '14px', fontWeight: '500' }}
                    ></TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontSize: '14px', fontWeight: '500' }}
                    >
                      Cervical
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontSize: '14px', fontWeight: '500' }}
                    >
                      Middle
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontSize: '14px', fontWeight: '500' }}
                    >
                      Incisal
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="center" sx={{ fontSize: '14px' }}>
                      Thickness
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontSize: '14px', whiteSpace: 'nowrap' }}
                    >
                      {selectedOutput?.thickness_cervical?.toFixed(2)} mm
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontSize: '14px', whiteSpace: 'nowrap' }}
                    >
                      {selectedOutput?.thickness_middle?.toFixed(2)} mm
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontSize: '14px', whiteSpace: 'nowrap' }}
                    >
                      {selectedOutput?.thickness_incisal?.toFixed(2)} mm
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}

        {selectedOutput?.instructions && (
          <Box sx={{ fontSize: '13px', lineHeight: 1.4, color: 'white' }}>
            <strong>Instructions:</strong> {selectedOutput.instructions}
          </Box>
        )}
      </Box>
    )
  },
)

const MaterialAdviceTooltip = React.memo(
  ({
    keyName,
    data,
    output,
    open,
    toggle,
    close,
    workflow,
    onDelete,
  }: {
    keyName: string
    data: any
    output: any
    open: boolean
    toggle: (key: string) => void
    close: (key: string) => void
    workflow: string
    onDelete: (key: string) => void
  }) => (
    <HtmlKonvaElement key={keyName} x={data?.x} y={data?.y}>
      <Box position="relative">
        <Box sx={{ transform: 'translate(-50%, -60%)' }}>
          <Tooltip
            open={open}
            title={
              output ? (
                <TooltipContent
                  output={output}
                  setTooltipOpen={() => close(keyName)}
                  workflow={workflow}
                />
              ) : null
            }
            arrow
            placement="right"
            slotProps={{
              popper: { disablePortal: true },
              tooltip: {
                sx: {
                  width: 394,
                  maxWidth: 'unset',
                  bgcolor: 'rgba(33, 33, 33, 0.80)',
                  color: 'white',
                  padding: 2,
                },
              },
              arrow: {
                sx: {
                  color: 'rgba(33, 33, 33, 0.80)',
                },
              },
            }}
          >
            <Chip
              onClick={() => toggle(keyName)}
              sx={{
                cursor: 'pointer',
                fontSize: '14px',
                color: 'black',
                padding: '4px 8px',
                background: '#68CEA1',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                lineHeight: '20px',
                '&:hover': {
                  background: '#68CEA1',
                },
                '& .MuiChip-label': {
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 8px 0 0px',
                  lineHeight: '20px',
                },
              }}
              label="Advice"
              onDelete={() => onDelete(keyName)}
            />
          </Tooltip>
        </Box>
      </Box>
    </HtmlKonvaElement>
  ),
)

export default MaterialAdviceTooltip
